<template>
  <v-app>
    <Navbar v-if="$route.meta.showHeader == true" />

    <v-main>
      <transition name="page-fade" mode="out-in">
        <router-view></router-view>
      </transition>
    </v-main>
    <Footer />

    <!-- Global Snackbar -->
    <v-snackbar
      v-model="$root.snackbar.show"
      :color="$root.snackbar.color"
      :timeout="3000"
      top
      right
      rounded="lg"
      elevation="8"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2" color="white">{{ $root.snackbar.icon }}</v-icon>
        {{ $root.snackbar.text }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="$root.snackbar.show = false" color="white">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
export default {
  name: "App",
  components: {
    Navbar,
    Footer,
  },

  data: () => ({}),
};
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap");

/* ===== Global Reset & Font ===== */
* {
  font-family: "Inter", sans-serif !important;
}

body {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* ===== Page Transition ===== */
.page-fade-enter-active,
.page-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.page-fade-enter {
  opacity: 0;
  transform: translateY(10px);
}
.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ===== Keyframe Animations ===== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-6px);
  }
}

/* ===== Animation Utility Classes ===== */
.animate-fadeInUp {
  animation: fadeInUp 0.6s ease forwards;
}

.animate-slideInLeft {
  animation: slideInLeft 0.5s ease forwards;
}

.animate-slideInRight {
  animation: slideInRight 0.5s ease forwards;
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

/* Staggered animation delays */
.delay-1 { animation-delay: 0.1s; opacity: 0; }
.delay-2 { animation-delay: 0.2s; opacity: 0; }
.delay-3 { animation-delay: 0.3s; opacity: 0; }
.delay-4 { animation-delay: 0.4s; opacity: 0; }
.delay-5 { animation-delay: 0.5s; opacity: 0; }
.delay-6 { animation-delay: 0.6s; opacity: 0; }

/* ===== Premium Card Styles ===== */
.premium-card {
  border-radius: 16px !important;
  transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}

.premium-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.04) !important;
  transform: translateY(-2px);
}

.premium-card-elevated {
  border-radius: 16px !important;
  transition: all 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08),
    0 1px 4px rgba(0, 0, 0, 0.04) !important;
}

.premium-card-elevated:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
  transform: translateY(-3px);
}

/* ===== Button Hover Effects ===== */
.hover-scale {
  transition: all 0.25s ease !important;
}

.hover-scale:hover {
  transform: scale(1.03) !important;
}

.hover-scale:active {
  transform: scale(0.98) !important;
}

.hover-glow-orange {
  transition: all 0.3s ease !important;
}

.hover-glow-orange:hover {
  box-shadow: 0 8px 25px rgba(246, 146, 39, 0.45) !important;
}

/* ===== Skeleton Loader ===== */
.skeleton-loader {
  background: linear-gradient(
    90deg,
    #f0f0f0 25%,
    #e0e0e0 50%,
    #f0f0f0 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 8px;
}

/* ===== Smooth Scrollbar ===== */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.15);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.25);
}

/* ===== Vuetify Overrides ===== */
.v-application .v-card {
  border-radius: 12px !important;
}

.v-application .v-btn {
  text-transform: none !important;
  letter-spacing: 0.01em !important;
  font-weight: 500 !important;
}

.v-application .v-chip {
  font-weight: 500 !important;
}

/* ===== Footer ===== */
footer {
  display: flex;
  align-items: center;
  height: 3rem;
}

.v-application p.rights {
  margin: 0;
  display: flex;
  align-items: center;
}

button.policy-btn {
  display: flex;
  align-items: center;
}

/* ===== Global Badge Dot ===== */
.unread-dot {
  width: 8px;
  height: 8px;
  background: #f69227;
  border-radius: 50%;
  display: inline-block;
}

/* ===== Section Titles ===== */
.section-title {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9e9e9e;
}
</style>
